import { BasicEmployeeView, SortableEmployeeField } from '@iota/inh-core-v1-management-client';

export type ExtractedCompanyEmployeeDetails = BasicEmployeeView & {
    officeName?: string;
    workingDays?: number;
    workingHours?: number;
    fte?: number;
    employeeGroupChangeDate?: string;
};
export type CompanyEmployeePropertiesForImport = Partial<Record<keyof ExtractedCompanyEmployeeDetails, string>>;

export const CompanyEmployeeKeyLabels: Required<CompanyEmployeePropertiesForImport> = {
    companyEmployeeId: $localize`Employee-ID`,
    companyId: $localize`Company-ID`,
    officeId: $localize`Office-ID`,
    firstName: $localize`First name`,
    lastName: $localize`Last name`,
    dateOfBirth: $localize`Date of birth`,
    employeeNumber: $localize`Employee-no.`,
    leavingDate: $localize`Leaving date`,
    licenseEndDate: $localize`License end`,
    registrationDateTime: $localize`Date of registration`,
    registeredEmployeeId: $localize`Employee-ID`,
    employeeGroup: $localize`Employee group`,
    yearlyBudget: $localize`Yearly budget`,
    registrationPossibleFrom: $localize`Registration possible from`,
    alternativeEmployeeNumber: $localize`Alternative Employee-no.`,
    userId: $localize`User-ID`,
    chaempId: 'chæmp-ID',
    registered: $localize`Registered`,
    licenseStillValid: $localize`License still valid`,

    officeName: $localize`Office`,
    workingDays: $localize`Working days per week`,
    workingHours: $localize`Weekly working hours`,
    fte: $localize`FTE`,
    employeeGroupChangeDate: $localize`Group change date`
};

export const CompanyEmployeeKeySuffixes: CompanyEmployeePropertiesForImport = {
    yearlyBudget: ' €'
};

export const CompanyEmployeeKeyToSortingKey: Partial<Record<keyof CompanyEmployeePropertiesForImport, SortableEmployeeField>> = {
    employeeNumber: SortableEmployeeField.EMPLOYEE_NUMBER,
    firstName: SortableEmployeeField.FIRST_NAME,
    lastName: SortableEmployeeField.LAST_NAME,
    dateOfBirth: SortableEmployeeField.DATE_OF_BIRTH,
    officeName: SortableEmployeeField.OFFICE_ID,
    registrationPossibleFrom: SortableEmployeeField.REGISTRATION_POSSIBLE_FROM,
    registrationDateTime: SortableEmployeeField.REGISTRATION_DATE_TIME,
    employeeGroup: SortableEmployeeField.EMPLOYEE_GROUP,
    yearlyBudget: SortableEmployeeField.YEARLY_BUDGET,
    leavingDate: SortableEmployeeField.LEAVING_DATE,
    licenseEndDate: SortableEmployeeField.LICENSE_END_DATE,
    alternativeEmployeeNumber: SortableEmployeeField.ALTERNATIVE_EMPLOYEE_NUMBER
};

export const EMPLOYEE_DATE_PROPERTIES: (keyof CompanyEmployeePropertiesForImport)[] = [
    'dateOfBirth',
    'leavingDate',
    'registrationDateTime',
    'registrationPossibleFrom',
    'employeeGroupChangeDate'
];
export const EMPLOYEE_PROPERTIES_WITH_CURRENCY: (keyof CompanyEmployeePropertiesForImport)[] = ['yearlyBudget'];

// All filters for the employee-overview
export enum SMART_EMPLOYEE_OVERVIEW_FILTER {
    ALL = 'all',
    REGISTERED = 'registered',
    NOT_REGISTERED = 'not_registered',
    FORMER = 'former'
}

export enum REGISTERED_EMPLOYEE_OVERVIEW_FILTER {
    REGISTERED = 'registered',
    FORMER = 'former'
}

export const RegisteredEmployeeOverviewFilterLabels: { [K in REGISTERED_EMPLOYEE_OVERVIEW_FILTER]: string } = {
    [REGISTERED_EMPLOYEE_OVERVIEW_FILTER.REGISTERED]: $localize`Active`,
    [REGISTERED_EMPLOYEE_OVERVIEW_FILTER.FORMER]: $localize`Former`
};

export const SmartEmployeeOverviewFilterLabels: { [K in SMART_EMPLOYEE_OVERVIEW_FILTER]: string } = {
    [SMART_EMPLOYEE_OVERVIEW_FILTER.ALL]: $localize`All`,
    [SMART_EMPLOYEE_OVERVIEW_FILTER.REGISTERED]: $localize`Registered`,
    [SMART_EMPLOYEE_OVERVIEW_FILTER.NOT_REGISTERED]: $localize`New`,
    [SMART_EMPLOYEE_OVERVIEW_FILTER.FORMER]: $localize`Former`
};

export const SmartEmployeeOverviewFilterTooltips: { [K in SMART_EMPLOYEE_OVERVIEW_FILTER]?: string } = {
    [SMART_EMPLOYEE_OVERVIEW_FILTER.ALL]: $localize`All active or new employees.`,
    [SMART_EMPLOYEE_OVERVIEW_FILTER.REGISTERED]: $localize`Registered and active employees with an account.`,
    [SMART_EMPLOYEE_OVERVIEW_FILTER.NOT_REGISTERED]: $localize`New employees that did not sign-up yet.`,
    [SMART_EMPLOYEE_OVERVIEW_FILTER.FORMER]: $localize`Former employees that have already left.`
};

const COMMON_COLUMNS: (keyof CompanyEmployeePropertiesForImport)[] = [
    'chaempId',
    'employeeNumber',
    'alternativeEmployeeNumber',
    'dateOfBirth',
    'firstName',
    'lastName',
    'officeName',
    'registrationPossibleFrom',
    'registrationDateTime',
    'employeeGroup',
    'yearlyBudget',
    'leavingDate',
    'licenseEndDate'
];

export const SmartEmployeeOverviewTableHeaders: {
    [K in SMART_EMPLOYEE_OVERVIEW_FILTER]: (keyof CompanyEmployeePropertiesForImport)[];
} = {
    [SMART_EMPLOYEE_OVERVIEW_FILTER.ALL]: [...COMMON_COLUMNS],
    [SMART_EMPLOYEE_OVERVIEW_FILTER.REGISTERED]: [...COMMON_COLUMNS],
    [SMART_EMPLOYEE_OVERVIEW_FILTER.NOT_REGISTERED]: COMMON_COLUMNS.filter(
        (column) => !['chaempId', 'firstName', 'lastName'].includes(column)
    ),
    [SMART_EMPLOYEE_OVERVIEW_FILTER.FORMER]: [...COMMON_COLUMNS]
};

export const SMART_EMPLOYEE_OVERVIEW_EMPLOYEE_IDS: (keyof CompanyEmployeePropertiesForImport)[] = [
    'companyEmployeeId',
    'registeredEmployeeId'
];

export interface SmartEmployeeOverviewFilterWithAdditionalInformation {
    key: SMART_EMPLOYEE_OVERVIEW_FILTER;
    label: string;
    employeeCount?: number;
    selected?: boolean;
}
